/**
 * v2 Chassis → Pending tab (GPON-only).
 *
 * Lists discovered-but-unprovisioned ONUs grouped by PON port and lets
 * the operator bulk-provision a whole port in one shot: pick the port,
 * configure one shared set of services (VNET / bandwidth / shaper /
 * cosq) via known-or-custom dropdowns sourced from the chassis's parsed
 * config profiles, and submit. The backend
 * (``reprovision-from-pending``) queues one ``provision_onu`` job per
 * serial with the shared ``services_override`` and fresh display/ONU
 * IDs picked gap-first from ``next-available``.
 *
 * This is the v2 port of v1's PendingOnusCard. Submitted jobs are handed
 * to the page's InlineJobTracker via the standard
 * ``inline-job-tracker.add`` custom event, and a per-serial results
 * table polls each job to terminal state inline.
 */
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { FileSearch, RefreshCw } from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { api, apiErrorMessage } from "../../../../api/client";
import { formatLocal } from "../../../../lib/datetime";
import { Badge } from "../../../components/Badge";
import { CommandPreview } from "../../../components/CommandPreview";
import { useConfirm } from "../../../components/ConfirmDialog";
import { FilterBar } from "../../../components/FilterBar";
import {
  missingProfileWarnings,
  ServiceSpecsEditor,
} from "../../../components/ServiceSpecsEditor";
import { useToast } from "../../../components/Toast";

type ProfileRow = {
  id: number;
  category: string;
  name: string;
  usage_count: number;
};
type ProfileListResponse = {
  profiles: ProfileRow[];
  latest_backup_id: number | null;
  parsed_profiles_at: string | null;
};

type PendingOnuRow = {
  serial: string;
  port: string | null;
  prior_port: string | null;
  prior_display_id: string | null;
  prior_event_type: string | null;
  prior_observed_at: string | null;
  // Set by the server when this serial reappeared because its latest
  // provision attempt didn't succeed (failed/partial/cancelled).
  last_provision_status: string | null;
  last_provision_job_id: string | null;
  last_provision_at: string | null;
  // Auto-provision watcher annotation (only meaningful when the site
  // has auto-provision enabled): failed auto-attempt count + parked
  // flag once it hits the site's max. Parked rows are skipped by the
  // watcher but stay manually provisionable here.
  auto_attempts: number;
  auto_parked: boolean;
};
type PendingListResponse = {
  job_id: string | null;
  completed_at: string | null;
  onus: PendingOnuRow[];
};

type NextAvailableIdsResponse = {
  port: string;
  next_display_id: number;
  next_onu_id: number;
  available: Array<[number, number]>;
};

type ReprovisionResolvedTarget = {
  serial: string;
  job_id: string | null;
  resolved_port: string | null;
  resolved_display_id: string | null;
  resolved_onu_id: number | null;
  reason: string | null;
};
type ReprovisionResponse = {
  targets: ReprovisionResolvedTarget[];
  queued: number;
  skipped: number;
};

type ServiceRow = {
  vnet_profile: string;
  upstream: string;
  downstream: string;
  cosq_profile: string;
};

const DEFAULT_SERVICES: ServiceRow[] = [
  {
    vnet_profile: "VNET30-PPPOE",
    upstream: "500M-burst",
    downstream: "1000M-burst",
    cosq_profile: "c0",
  },
];

const selectCls =
  "w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 font-mono text-xs text-slate-100 focus-visible:border-emerald-700 focus-visible:outline-none";

// Prior lifecycle events meaning the device was DELIBERATELY removed by an
// operator — render those rows calm ("cleared — awaiting reprovision"), not
// as amber failed-attempt noise.
const CLEARED_PRIOR_EVENTS = new Set(["deprovisioned", "bulk_cleared"]);

const TERMINAL_STATUSES = new Set([
  "succeeded",
  "failed",
  "partial",
  "cancelled",
]);

export function PendingTab({
  chassisId,
  adapterKey,
  gigafyLocationId,
}: {
  chassisId: string;
  adapterKey: string;
  gigafyLocationId?: string | null;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const [lastResult, setLastResult] = useState<ReprovisionResponse | null>(
    null,
  );

  // Persist the operator's last-used service combination per chassis
  // (matches v1/v3 so reopening the tab remembers what was provisioned).
  const servicesKey = `chassis-${chassisId}-services`;
  const [services, setServices] = useState<ServiceRow[]>(() => {
    try {
      const raw = localStorage.getItem(servicesKey);
      if (raw) {
        const parsed = JSON.parse(raw) as ServiceRow[];
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {
      /* ignore */
    }
    return DEFAULT_SERVICES.map((s) => ({ ...s }));
  });
  useEffect(() => {
    try {
      localStorage.setItem(servicesKey, JSON.stringify(services));
    } catch {
      /* quota / private-mode — ignore */
    }
  }, [servicesKey, services]);

  const [selectedPort, setSelectedPort] = useState<string>("");

  // Available profile names per category. SelectOrCustom accepts a value
  // not in the list as Custom… so a saved profile that no longer appears
  // in the parsed cache still selects correctly.
  const profilesQuery = useQuery<ProfileListResponse>({
    queryKey: ["v2", "chassis", chassisId, "profiles"],
    queryFn: async () => {
      const res = await api.get<ProfileListResponse>(
        `/chassis/${chassisId}/profiles`,
      );
      return res.data;
    },
  });
  const profilesByCategory = (cat: string): string[] => {
    const list = profilesQuery.data?.profiles ?? [];
    return Array.from(
      new Set(list.filter((p) => p.category === cat).map((p) => p.name)),
    ).sort();
  };
  const knownVnetProfiles = profilesByCategory("vnet");
  const knownBandwidthProfiles = profilesByCategory("bandwidth");
  const knownShaperProfiles = profilesByCategory("shaper");
  const knownCosqProfiles = profilesByCategory("cosq");

  // Chassis-derived cosq default: the most-used cosq profile in the
  // parsed backup. The old hardcoded "c0" kept pre-filling a profile
  // that doesn't exist on chassis whose OLT names its queues
  // differently.
  const defaultCosqProfile = useMemo<string | null>(() => {
    const cosq = (profilesQuery.data?.profiles ?? []).filter(
      (p) => p.category === "cosq",
    );
    if (cosq.length === 0) return null;
    return [...cosq].sort(
      (a, b) => b.usage_count - a.usage_count || a.name.localeCompare(b.name),
    )[0].name;
  }, [profilesQuery.data]);

  // Self-heal the hardcoded "c0" sentinel (including one persisted to
  // localStorage by an earlier session) once the parsed profiles show
  // this OLT has no "c0". Deliberate custom values are left alone —
  // only the literal "c0" is rewritten, and only when it isn't a real
  // profile here.
  useEffect(() => {
    if (!defaultCosqProfile || knownCosqProfiles.includes("c0")) return;
    setServices((arr) =>
      arr.some((s) => s.cosq_profile === "c0")
        ? arr.map((s) =>
            s.cosq_profile === "c0"
              ? { ...s, cosq_profile: defaultCosqProfile }
              : s,
          )
        : arr,
    );
    // knownCosqProfiles derives from the same query data as
    // defaultCosqProfile, so the single dep is sufficient.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCosqProfile]);

  // Age of the parsed-profiles cache — the dropdowns are only as fresh
  // as the last backup's inline reparse, so surface that next to the
  // services editor.
  const profilesAgeDays = useMemo<number | null>(() => {
    const iso = profilesQuery.data?.parsed_profiles_at;
    if (!iso) return null;
    const ms = Date.now() - new Date(iso).getTime();
    if (!Number.isFinite(ms) || ms < 0) return 0;
    return Math.floor(ms / 86_400_000);
  }, [profilesQuery.data]);

  // Site auto-provision state — powers the "Auto-provision ON" pill so
  // an operator opening the tab knows the watcher is already handling
  // this site's pending devices.
  const siteDefaultsQuery = useQuery<{
    auto_provision_enabled: boolean;
    boost_active: boolean;
  }>({
    queryKey: ["v2", "site-provision-defaults", gigafyLocationId, adapterKey],
    enabled: !!gigafyLocationId,
    queryFn: async () => {
      const res = await api.get(
        `/locations/${gigafyLocationId}/provision-defaults`,
        { params: { adapter_key: adapterKey } },
      );
      return res.data;
    },
  });
  const autoProvisionOn =
    siteDefaultsQuery.data?.auto_provision_enabled === true;
  const boostActive = siteDefaultsQuery.data?.boost_active === true;

  const knownProfiles = useMemo(
    () => ({
      vnet: knownVnetProfiles,
      bandwidth: knownBandwidthProfiles,
      shaper: knownShaperProfiles,
      cosq: knownCosqProfiles,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profilesQuery.data],
  );

  const query = useQuery<PendingListResponse>({
    queryKey: ["v2", "chassis", chassisId, "pending"],
    queryFn: async () => {
      const res = await api.get<PendingListResponse>(
        `/chassis/${chassisId}/onus/pending`,
      );
      return res.data;
    },
  });

  // The pending list is server-authoritative: the backend hides serials with
  // an in-flight (queued/running) or just-succeeded provision_onu job and
  // keeps (flagged) the ones whose provision failed. Queuing a provision
  // invalidates this query, and the just-queued jobs read as 'queued', so the
  // serials drop on the next refetch — no client-side optimistic set needed
  // (the old session-only set never reverted on failure).
  const onus = useMemo(() => query.data?.onus ?? [], [query.data]);
  const portGroups = useMemo<Record<string, PendingOnuRow[]>>(() => {
    const m: Record<string, PendingOnuRow[]> = {};
    for (const o of onus) {
      const p = o.port ?? "(unknown)";
      (m[p] ??= []).push(o);
    }
    return m;
  }, [onus]);
  const portOptions = useMemo(
    () =>
      Object.entries(portGroups)
        .map(([port, list]) => ({ port, count: list.length }))
        .sort((a, b) =>
          a.port.localeCompare(b.port, undefined, { numeric: true }),
        ),
    [portGroups],
  );

  useEffect(() => {
    // Re-pick the port whenever the current selection isn't a valid option:
    // either nothing's chosen yet (initial load), OR the chosen port was just
    // fully provisioned and dropped out of Pending. Advancing here — instead
    // of leaving a stale selectedPort pointing at a gone port — makes the
    // devices list, assigned IDs and command preview recompute exactly as
    // they do on a manual dropdown pick (the bug was that a non-empty-but-
    // gone selectedPort short-circuited this effect). "" when nothing's left.
    if (portOptions.some((o) => o.port === selectedPort)) return;
    setSelectedPort(portOptions.length > 0 ? portOptions[0].port : "");
  }, [portOptions, selectedPort]);

  const portSerials = useMemo(
    () => (selectedPort ? portGroups[selectedPort] ?? [] : []),
    [portGroups, selectedPort],
  );

  // Display-only find aid (shared FilterBar): narrows which rows the
  // devices list SHOWS — the provision batch always covers the whole
  // selected port (portSerials), never the filtered subset.
  const [deviceFilter, setDeviceFilter] = useState("");
  const visibleSerials = useMemo(() => {
    const needle = deviceFilter.trim().toLowerCase();
    if (!needle) return portSerials;
    return portSerials.filter((o) =>
      [o.serial, o.prior_port, o.prior_display_id].some((v) =>
        (v ?? "").toLowerCase().includes(needle),
      ),
    );
  }, [portSerials, deviceFilter]);

  // Pull next-available IDs whenever the port changes — gap-aware, count
  // = number of pending serials on the port (clamped 1..128 server-side, the
  // GPON per-PON ONU max; must cover a full port or the Provision button —
  // gated on IDs assigned == device count — never enables).
  const idsQuery = useQuery<NextAvailableIdsResponse | null>({
    queryKey: [
      "v2",
      "chassis",
      chassisId,
      "next-available",
      selectedPort,
      portSerials.length,
    ],
    queryFn: async () => {
      if (!selectedPort || portSerials.length === 0) return null;
      const count = Math.min(128, Math.max(1, portSerials.length));
      const res = await api.get<NextAvailableIdsResponse>(
        `/chassis/${chassisId}/onus/next-available?port=${encodeURIComponent(
          selectedPort,
        )}&count=${count}`,
      );
      return res.data;
    },
    enabled: !!selectedPort && portSerials.length > 0,
  });
  const assignedPairs: Array<[number, number]> = useMemo(() => {
    const all = idsQuery.data?.available ?? [];
    return all.slice(0, portSerials.length);
  }, [idsQuery.data, portSerials.length]);
  const displayIdsToAssign = assignedPairs.map(([d]) => d);
  const onuIdsToAssign = assignedPairs.map(([, o]) => o);

  // Scan job in flight (queued via the "Scan for devices" button). Polled to
  // its terminal state below — replaces the old fixed 5s timer, which
  // refetched too early on a busy chassis (the scan hadn't landed yet) and
  // left the operator staring at a stale list.
  const [scanJobId, setScanJobId] = useState<string | null>(null);
  const scanJobQuery = useQuery<{ status: string }>({
    queryKey: ["v2", "chassis", chassisId, "scan-job", scanJobId],
    enabled: !!scanJobId,
    queryFn: async () => {
      const res = await api.get<{ status: string }>(`/jobs/${scanJobId}`);
      return res.data;
    },
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      if (s && TERMINAL_STATUSES.has(s)) return false;
      return 2_000;
    },
  });
  const scanStatus = scanJobQuery.data?.status;
  useEffect(() => {
    if (!scanJobId || !scanStatus || !TERMINAL_STATUSES.has(scanStatus)) {
      return;
    }
    setScanJobId(null);
    // The scan landed (or failed) — refetch the list AND the home-badge
    // count so the two stay in sync.
    queryClient.invalidateQueries({
      queryKey: ["v2", "chassis", chassisId, "pending"],
    });
    queryClient.invalidateQueries({
      queryKey: ["v2", "chassis-pending-summary"],
    });
  }, [scanJobId, scanStatus, chassisId, queryClient]);

  const refresh = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ id: string }>(`/chassis/${chassisId}/jobs`, {
        name: "list_pending_onus",
        params: {},
      });
      return res.data;
    },
    onSuccess: ({ id }) => {
      window.dispatchEvent(
        new CustomEvent("inline-job-tracker.add", {
          detail: { jobId: id, kind: "list_pending_onus" },
        }),
      );
      toast.push("Device scan queued", "success");
      setScanJobId(id);
    },
    onError: () => toast.push("Scan failed to queue", "error"),
  });

  // Profiles in the current Services panel that aren't in the parsed OLT
  // cache. Submitting an unknown profile risks a CBAC-side "resource does
  // not exist" failure that can kill the SSH session mid-paste.
  const missingProfiles = useMemo<string[]>(
    () => missingProfileWarnings(services, knownProfiles),
    [services, knownProfiles],
  );

  // Shared service list (trimmed, empty-VNET rows dropped) — used by both
  // the live submit and the command preview so the two never diverge.
  const servicesOut = useMemo(
    () =>
      services
        .filter((s) => s.vnet_profile.trim() !== "")
        .map((s) => ({
          vnet_profile: s.vnet_profile.trim(),
          ...(s.upstream.trim() ? { upstream: s.upstream.trim() } : {}),
          ...(s.downstream.trim() ? { downstream: s.downstream.trim() } : {}),
          ...(s.cosq_profile.trim()
            ? { cosq_profile: s.cosq_profile.trim() }
            : {}),
        })),
    [services],
  );

  // Client-driven command preview: render each device's provision_onu
  // block via the per-op preview endpoint (one call per device, exactly
  // what the batch will queue) and concatenate so the operator can review
  // + copy the whole port's CLI before submitting.
  const [cliPreview, setCliPreview] = useState<string | null>(null);
  const previewMut = useMutation({
    mutationFn: async () => {
      const blocks = await Promise.all(
        portSerials.map(async (o, i) => {
          const did = displayIdsToAssign[i];
          const oid = onuIdsToAssign[i];
          if (did == null) return null;
          const res = await api.post<{ rendered_block: string }>(
            `/chassis/${chassisId}/maintenance/provision-onu/preview`,
            {
              port: selectedPort,
              display_id: String(did),
              onu_id: oid ?? null,
              serial: o.serial,
              services: servicesOut,
            },
          );
          return `! ── ${o.serial} → SFPPON-${selectedPort}/${did} ──\n${res.data.rendered_block}`;
        }),
      );
      return blocks.filter(Boolean).join("\n\n");
    },
    onSuccess: (text) => setCliPreview(text),
    onError: () => toast.push("Preview failed — check the inputs", "error"),
  });

  // Stale the rendered preview whenever the port, assigned IDs, or
  // services change — a preview that no longer matches the inputs would
  // mislead the operator about what's about to run.
  useEffect(() => {
    setCliPreview(null);
  }, [selectedPort, assignedPairs, servicesOut]);

  // Submit: one provision_onu per serial, fresh locations baked from the
  // assigned ID pairs. All serials in the batch share the same services.
  const reprovision = useMutation({
    mutationFn: async () => {
      if (missingProfiles.length > 0) {
        const proceed = await confirm({
          title: "Profiles not on OLT",
          summary: [
            "These profiles aren't in the OLT's parsed config:",
            "",
            ...missingProfiles.map((m) => `  • ${m}`),
            "",
            "Provisioning with an unknown profile typically causes Radisys CBAC to drop the SSH session mid-paste with no visible error.",
          ].join("\n"),
          confirmLabel: "Submit anyway",
        });
        if (!proceed) {
          throw new Error("cancelled by operator (profiles not on OLT)");
        }
      }
      const serials = portSerials.map((o) => o.serial);
      const fresh: Record<string, { port: string; display_id: string }> = {};
      serials.forEach((s, i) => {
        const did = displayIdsToAssign[i];
        if (did != null) {
          fresh[s] = { port: selectedPort, display_id: String(did) };
        }
      });
      const body: {
        serials: string[];
        services_override?: typeof servicesOut;
        fresh_locations?: typeof fresh;
      } = { serials };
      if (servicesOut.length > 0) body.services_override = servicesOut;
      if (Object.keys(fresh).length > 0) body.fresh_locations = fresh;
      // WS-K2 — the endpoint queues ONE scan-then-provision arm job: a
      // fresh pending scan runs first and serials no longer pending are
      // dropped (the 2026-07-20 stale-snapshot double-provision). Poll
      // the arm to terminal, then read the per-serial outcome from its
      // summary result — same ReprovisionResponse shape as before.
      const res = await api.post<{ job_id: string; requested: number }>(
        `/chassis/${chassisId}/onus/reprovision-from-pending`,
        body,
      );
      const armJobId = res.data.job_id;
      window.dispatchEvent(
        new CustomEvent("inline-job-tracker.add", {
          detail: { jobId: armJobId, kind: "provision_from_pending" },
        }),
      );
      const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
      let armStatus = "queued";
      for (let i = 0; i < 90 && !TERMINAL_STATUSES.has(armStatus); i++) {
        await sleep(2_000);
        const j = await api.get<{ status: string }>(`/jobs/${armJobId}`);
        armStatus = j.data.status;
      }
      if (!TERMINAL_STATUSES.has(armStatus)) {
        throw new Error(
          "scan-then-provision is still running — check Jobs for the outcome",
        );
      }
      const resultsRes = await api.get<{
        results: Array<{
          target: string;
          parsed_data: ReprovisionResponse | null;
        }>;
      }>(`/jobs/${armJobId}/results`);
      const summary = resultsRes.data.results.find(
        (r) => r.target === "(provision-from-pending)" && r.parsed_data,
      )?.parsed_data;
      if (!summary) {
        throw new Error(
          armStatus === "succeeded"
            ? "scan completed but no provisioning summary was written"
            : `pending scan ${armStatus} — no batches were queued (see the job in Jobs)`,
        );
      }
      return summary;
    },
    onSuccess: (d) => {
      setLastResult(d);
      setCliPreview(null);
      // No optimistic client-side hiding: the pending-query invalidate below
      // refetches, and the server hides the just-queued serials (their jobs
      // read as 'queued'). Skipped serials stay visible so the operator sees
      // why.
      // Surface every queued job in the page's inline tracker — deduped,
      // since a batch job covers several serials (they share one job_id).
      for (const jobId of new Set(
        d.targets.map((t) => t.job_id).filter((j): j is string => !!j),
      )) {
        window.dispatchEvent(
          new CustomEvent("inline-job-tracker.add", {
            detail: { jobId, kind: "provision_onu_batch" },
          }),
        );
      }
      const dropped = d.targets.filter(
        (t) => t.reason === "no_longer_pending",
      ).length;
      const otherSkips = d.skipped - dropped;
      toast.push(
        `Queued ${d.queued} provision job${d.queued === 1 ? "" : "s"}` +
          (dropped
            ? ` · ${dropped} dropped by fresh scan (no longer pending)`
            : "") +
          (otherSkips > 0 ? ` · ${otherSkips} skipped` : ""),
        d.queued > 0 ? "success" : "info",
      );
      queryClient.invalidateQueries({
        queryKey: ["v2", "chassis", chassisId, "pending"],
      });
      queryClient.invalidateQueries({
        queryKey: ["v2", "chassis", chassisId, "next-available"],
      });
    },
    onError: (err) =>
      toast.push(
        apiErrorMessage(err, "Provision failed to queue"),
        "error",
      ),
  });

  const hasVnet = services.some((s) => s.vnet_profile.trim() !== "");
  const canProvision =
    portSerials.length > 0 &&
    displayIdsToAssign.length === portSerials.length &&
    hasVnet &&
    !reprovision.isPending;

  return (
    <section className="space-y-3 rounded-lg bg-slate-900 shadow-border p-4">
      <div className="flex items-baseline justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-medium text-slate-200">
            Pending ONUs — bulk provision per port
            {autoProvisionOn && (
              // Same-page hash deep-link into Administration → Auto
              // provision (hashchange routes the tab switch).
              <a
                href="#tab=admin&sub=auto-provision"
                title="Open Administration → Auto provision"
              >
                <Badge tone={boostActive ? "warning" : "success"}>
                  {boostActive ? "Auto-provision ON · boosted" : "Auto-provision ON"}
                </Badge>
              </a>
            )}
          </h3>
          <p className="text-[10px] text-slate-500">
            Discovered-but-unprovisioned ONUs from the latest{" "}
            <code className="font-mono">list_pending_onus</code> run. Pick a
            port, set the services once, and provision the whole port.
            {autoProvisionOn && (
              <>
                {" "}
                This site's watcher is also provisioning new devices
                automatically — see Administration → Auto provision.
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-slate-500">
            {onus.length} pending
            {query.data?.completed_at ? (
              <>
                {" "}
                · collected {formatLocal(query.data.completed_at)}
              </>
            ) : null}
          </span>
          <button
            type="button"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending || !!scanJobId}
            className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-50"
            title="Queue a list_pending_onus job. The list refreshes when it lands."
          >
            <RefreshCw
              className={`h-3 w-3 ${scanJobId ? "animate-spin" : ""}`}
            />
            {refresh.isPending
              ? "Queuing…"
              : scanJobId
                ? "Scanning…"
                : "Scan for devices"}
          </button>
        </div>
      </div>

      {query.isLoading ? (
        <p className="text-xs text-slate-500">Loading…</p>
      ) : query.isError ? (
        <p className="text-xs text-red-400">
          Failed to load pending ONUs. Try <em>Scan for devices</em>.
        </p>
      ) : onus.length === 0 ? (
        <p className="text-xs text-slate-500">
          No pending ONUs. Click <em>Scan for devices</em> if the chassis may
          have new unprovisioned hardware.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* LEFT — Pending ONUs panel */}
          <div className="space-y-2 rounded border border-slate-800 bg-slate-950/40 p-3">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-300">
              Pending ONUs
            </h4>
            <label className="block space-y-1 text-xs">
              <span className="block text-slate-400">PON Port</span>
              <select
                value={selectedPort}
                onChange={(e) => setSelectedPort(e.target.value)}
                className={selectCls}
              >
                {portOptions.map((p) => (
                  <option key={p.port} value={p.port}>
                    SFPPON-{p.port} ({p.count} device{p.count === 1 ? "" : "s"})
                  </option>
                ))}
              </select>
            </label>
            <div className="space-y-1 text-xs">
              <span className="block text-slate-400">
                Devices to provision ({portSerials.length})
              </span>
              <FilterBar
                value={deviceFilter}
                onChange={setDeviceFilter}
                placeholder="Find a serial…"
                className="w-full"
              />
              <div className="max-h-32 overflow-auto rounded border border-slate-700 bg-slate-950 px-2 py-1 font-mono text-[11px] text-slate-200">
                {visibleSerials.map((o) => (
                  <div key={o.serial} className="flex items-center gap-2">
                    <span>{o.serial}</span>
                    {o.prior_port && (
                      <span className="text-[10px] uppercase tracking-wide text-slate-500">
                        prior: {o.prior_port}
                        {o.prior_display_id ? `/${o.prior_display_id}` : ""}
                      </span>
                    )}
                    {!o.last_provision_status &&
                      o.prior_event_type &&
                      CLEARED_PRIOR_EVENTS.has(o.prior_event_type) && (
                        <span
                          className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-400"
                          title="Deliberately removed by an operator — provision it here when ready"
                        >
                          cleared — awaiting reprovision
                        </span>
                      )}
                    {o.last_provision_status &&
                      (o.last_provision_job_id ? (
                        <Link
                          to={`/settings/jobs/${o.last_provision_job_id}`}
                          className="rounded bg-amber-950 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-300 hover:bg-amber-900"
                          title={`Previous provision ${o.last_provision_status} — click for the job`}
                        >
                          previous attempt failed — retry
                        </Link>
                      ) : (
                        <span className="rounded bg-amber-950 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-300">
                          previous attempt failed — retry
                        </span>
                      ))}
                    {o.auto_parked && (
                      <span
                        className="rounded bg-red-950 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-red-300"
                        title="Auto-provision skips this serial now — provision it manually here (that resets nothing; manual attempts are never counted)."
                      >
                        parked after {o.auto_attempts} auto attempts
                      </span>
                    )}
                  </div>
                ))}
                {visibleSerials.length < portSerials.length && (
                  <div className="text-[10px] text-slate-500">
                    … {portSerials.length - visibleSerials.length} more hidden
                    by the filter (still included in the batch)
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-1 text-xs">
              <span className="block text-slate-400">
                Display IDs to assign (fills gaps first)
              </span>
              <div className="min-h-[28px] rounded border border-slate-700 bg-slate-950 px-2 py-1 font-mono text-[11px] text-slate-200">
                {idsQuery.isLoading
                  ? "…"
                  : displayIdsToAssign.length > 0
                    ? displayIdsToAssign.join(", ")
                    : "—"}
              </div>
            </div>
            <div className="space-y-1 text-xs">
              <span className="block text-slate-400">
                ONU IDs to assign (fills gaps first)
              </span>
              <div className="min-h-[28px] rounded border border-slate-700 bg-slate-950 px-2 py-1 font-mono text-[11px] text-slate-200">
                {idsQuery.isLoading
                  ? "…"
                  : onuIdsToAssign.length > 0
                    ? onuIdsToAssign.join(", ")
                    : "—"}
              </div>
            </div>
          </div>

          {/* RIGHT — Services panel (shared editor, also used by the
              site Auto-provision template) */}
          <div className="rounded border border-slate-800 bg-slate-950/40 p-3">
            <ServiceSpecsEditor
              services={services}
              onChange={setServices}
              known={knownProfiles}
              defaultCosqProfile={defaultCosqProfile}
              noParsedProfiles={profilesQuery.data?.latest_backup_id == null}
            />
            {profilesAgeDays != null && (
              <p className="mt-2 text-[10px] text-slate-500">
                Profiles from backup{" "}
                {profilesAgeDays === 0
                  ? "today"
                  : `${profilesAgeDays} day${
                      profilesAgeDays === 1 ? "" : "s"
                    } ago`}{" "}
                — take a fresh backup to refresh them.
              </p>
            )}
          </div>
        </div>
      )}

      {onus.length > 0 && (
        <div className="space-y-2 rounded border border-slate-800 bg-slate-950/40 p-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-300">
            Provision
          </h4>
          <p className="text-[11px] text-slate-500">
            One <code className="font-mono">provision_onu</code> job per device
            in the batch — each on its own command_jobs row so a single
            failure stays isolated. The worker renders the active{" "}
            <Link
              to="/settings/templates"
              className="underline decoration-dotted hover:text-slate-200"
            >
              provision_onu template
            </Link>
            {adapterKey ? ` for ${adapterKey}` : ""}.
          </p>

          {missingProfiles.length > 0 && (
            <div className="rounded border border-amber-700/60 bg-amber-950/30 p-2 text-[11px] text-amber-200">
              <strong>Warning:</strong> {missingProfiles.length} profile
              {missingProfiles.length === 1 ? " is" : "s are"} not in the OLT's
              parsed config. Submitting pops a confirm dialog first — but the
              usual cause is a typo (e.g. "VNET-31-DHCP" vs "VNET31-DHCP"). Fix
              it here before provisioning.
              <ul className="mt-1 list-disc pl-5">
                {missingProfiles.slice(0, 6).map((m) => (
                  <li key={m} className="font-mono">
                    {m}
                  </li>
                ))}
                {missingProfiles.length > 6 && (
                  <li className="opacity-70">
                    +{missingProfiles.length - 6} more…
                  </li>
                )}
              </ul>
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => previewMut.mutate()}
              disabled={
                previewMut.isPending ||
                portSerials.length === 0 ||
                displayIdsToAssign.length !== portSerials.length
              }
              className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-40"
              title="Render the exact provision_onu CLI for every device on this port"
            >
              <FileSearch className="h-3 w-3" />
              {previewMut.isPending ? "Rendering…" : "Preview commands"}
            </button>
            <button
              type="button"
              onClick={() => reprovision.mutate()}
              disabled={!canProvision}
              className="rounded bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-30"
            >
              {reprovision.isPending
                ? "Queuing…"
                : `Provision ${portSerials.length} ONU${portSerials.length === 1 ? "" : "s"}`}
            </button>
            {!hasVnet && (
              <span className="text-[11px] text-amber-300">
                pick at least one VNET to enable
              </span>
            )}
            {reprovision.isError && (
              <span className="text-xs text-red-300">
                {apiErrorMessage(reprovision.error, String(reprovision.error))}
              </span>
            )}
          </div>

          {cliPreview && (
            <CommandPreview
              text={cliPreview}
              label={`Commands to run — ${portSerials.length} device${
                portSerials.length === 1 ? "" : "s"
              }`}
            />
          )}
        </div>
      )}

      {lastResult && <ResultsPanel result={lastResult} />}
      {confirmDialog}
    </section>
  );
}

// ---------------------------------------------------------------------
// ResultsPanel — live status + inline failure preview for the N
// provision_onu jobs just queued. Each row polls its own
// /jobs/{id}/results every 2s until it reaches a terminal state.
// ---------------------------------------------------------------------

type JobResult = {
  id: number;
  target: string;
  status: string;
  raw_output: string | null;
  parsed_data: { error?: string | null } | null;
};
type JobMeta = { status: string };
type JobResultsResponse = { job: JobMeta; results: JobResult[] };

// The worker writes each ONU's command_results row keyed on this target.
function jobTargetKey(t: ReprovisionResolvedTarget): string {
  return `SFPPON-${t.resolved_port}/${t.resolved_display_id}-ONU`;
}

function extractFailureLine(results: JobResult[]): string | null {
  const re = /(?:^|\s)(Failed to[^\n]*|Error:[^\n]*|[^\n]*\bfailure\b[^\n]*)/i;
  for (const r of results) {
    if (r.status !== "error" || !r.raw_output) continue;
    for (const rawLine of r.raw_output.split(/\r?\n/)) {
      const m = rawLine.match(re);
      if (!m) continue;
      const cleaned = m[1].replace(/^CBAC\([^)]+\)#\s*/g, "").trim();
      if (cleaned) return cleaned;
    }
  }
  return null;
}

function ResultsPanel({ result }: { result: ReprovisionResponse }) {
  // One batch job can cover several serials, so poll each UNIQUE job once
  // (not once per serial) and read per-serial outcomes from its results.
  const jobIds = useMemo(
    () =>
      [
        ...new Set(
          result.targets.map((t) => t.job_id).filter((j): j is string => !!j),
        ),
      ],
    [result],
  );
  const queries = useQueries({
    queries: jobIds.map((jid) => ({
      queryKey: ["v2", "job-status", jid],
      queryFn: async () => {
        const res = await api.get<JobResultsResponse>(`/jobs/${jid}/results`);
        return res.data;
      },
      refetchInterval: (q: { state: { data?: JobResultsResponse } }) => {
        const s = q.state.data?.job?.status;
        if (!s) return 2000;
        return TERMINAL_STATUSES.has(s) ? false : 2000;
      },
    })),
  });

  const byJob = useMemo(() => {
    const m = new Map<string, JobResultsResponse | undefined>();
    jobIds.forEach((jid, i) => m.set(jid, queries[i]?.data));
    return m;
  }, [jobIds, queries]);

  // A serial's status: prefer its own command_results row (success/error),
  // else fall back to the job's overall status while it's still running.
  const serialStatus = (t: ReprovisionResolvedTarget): string | null => {
    if (!t.job_id) return null;
    const data = byJob.get(t.job_id);
    if (!data) return null;
    const row = data.results.find((r) => r.target === jobTargetKey(t));
    if (row) return row.status === "success" ? "succeeded" : "failed";
    return data.job?.status ?? null;
  };

  const serialFailure = (t: ReprovisionResolvedTarget): string | null => {
    if (!t.job_id) return null;
    const data = byJob.get(t.job_id);
    if (!data) return null;
    const row = data.results.find((r) => r.target === jobTargetKey(t));
    if (row?.status === "error" && row.parsed_data?.error) {
      return row.parsed_data.error;
    }
    // Fall back to scraping an OLT error line from the (first-row) transcript.
    return extractFailureLine(data.results);
  };

  const liveCounts = useMemo(() => {
    let succeeded = 0;
    let failed = 0;
    let running = 0;
    for (const t of result.targets) {
      if (!t.job_id) continue;
      const s = serialStatus(t);
      if (s === "succeeded") succeeded += 1;
      else if (s === "failed" || s === "partial") failed += 1;
      else running += 1;
    }
    return { succeeded, failed, running };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, byJob]);

  return (
    <div className="rounded border border-slate-800 bg-slate-950/60 p-3 text-xs">
      <p className="mb-2 text-slate-300">
        Queued <strong className="text-emerald-300">{result.queued}</strong>{" "}
        job{result.queued === 1 ? "" : "s"} · skipped{" "}
        <strong className="text-amber-300">{result.skipped}</strong>
        {result.queued > 0 && (
          <>
            {" · "}
            <span className="text-emerald-400">
              {liveCounts.succeeded} succeeded
            </span>
            {" · "}
            <span className="text-red-300">{liveCounts.failed} failed</span>
            {liveCounts.running > 0 && (
              <>
                {" · "}
                <span className="text-sky-300">
                  {liveCounts.running} running
                </span>
              </>
            )}
          </>
        )}
      </p>
      <table className="w-full text-[11px]">
        <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-2 py-0.5">Serial</th>
            <th className="px-2 py-0.5">Port</th>
            <th className="px-2 py-0.5">Display ID</th>
            <th className="px-2 py-0.5">Status</th>
            <th className="px-2 py-0.5">Job</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800/40 text-slate-300">
          {result.targets.map((t) => {
            const status = serialStatus(t);
            const failureLine = serialFailure(t);
            return (
              <Fragment key={t.serial}>
                <tr>
                  <td className="px-2 py-0.5 font-mono">{t.serial}</td>
                  <td className="px-2 py-0.5 font-mono text-slate-400">
                    {t.resolved_port ?? "—"}
                  </td>
                  <td className="px-2 py-0.5 font-mono text-slate-400">
                    {t.resolved_display_id ?? "—"}
                  </td>
                  <td className="px-2 py-0.5">
                    {!t.job_id ? (
                      <span className="rounded border border-amber-700/60 bg-amber-900/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-200">
                        skipped
                      </span>
                    ) : status === null ? (
                      <span className="text-slate-500">…</span>
                    ) : status === "succeeded" ? (
                      <span className="rounded border border-emerald-700 bg-emerald-900/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-emerald-200">
                        succeeded
                      </span>
                    ) : status === "failed" || status === "partial" ? (
                      <span className="rounded border border-red-700 bg-red-900/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-red-200">
                        {status}
                      </span>
                    ) : (
                      <span className="rounded border border-sky-700 bg-sky-900/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-sky-200">
                        {status}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-0.5 font-mono text-[10px]">
                    {t.job_id ? (
                      <Link
                        to={`/settings/jobs/${t.job_id}`}
                        className="text-emerald-400 hover:text-emerald-300"
                      >
                        {t.job_id.slice(0, 8)}
                      </Link>
                    ) : (
                      <span className="text-amber-300">{t.reason}</span>
                    )}
                  </td>
                </tr>
                {failureLine && (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-2 pb-1.5 pt-0 font-mono text-[10px] text-red-200"
                    >
                      <span className="mr-2 inline-block min-w-[8ch] text-red-300/80">
                        ↳ OLT:
                      </span>
                      {failureLine}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
